<template>
    <div class="wrap-content">
        <div class="filter-section row-min-space">
            <div class="row">
                <div class="col-md-6 filter-title-section">
                    <h2>Roles &amp; Permissions</h2>
                </div>
                <div class="col-md-6 text-right">
                    <div class="row">
                        <div class="col-md-6 col-xs-12">
                            <div class="form-group search-input">
                                <i class="icon-search"></i>
                                <b-form-input
                                    v-model="query.keyword"
                                    :placeholder="'Search role'"
                                    autocomplete="no"
                                    @keyup.enter="applySearch('search')"
                                ></b-form-input>
                            </div>
                        </div>
                        <div class="col-md-6 col-xs-12 filter-btn-section">
                            <div class="row">
                                <div class="col-md-5">
                                    <button
                                        class="btn btn-primary"
                                        type="submit"
                                        :class="
                                            loading && showPageLoader
                                                ? 'disabled'
                                                : ''
                                        "
                                        @click="applySearch('search')"
                                    >
                                        <span class="btn-text">Apply</span>
                                        <span class="loader"></span>
                                    </button>
                                </div>
                                <div class="col-md-7">
                                    <b-button
                                        v-b-modal.add-role-popup
                                        class="btn secondary-add-btn"
                                        variant="secondary"
                                        type="submit"
                                        :class="{
                                            'button-disabled':
                                                !hasScopes('manage-roles'),
                                        }"
                                    >
                                        <span class="btn-text">+ Add Role</span>
                                    </b-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- user management table -->
        <div
            :class="[
                'table-section',
                'tablet-table-xs-min-width',
                {
                    'overflow-hidden': !items.length && !showPageLoader,
                },
            ]"
        >
            <b-table
                no-local-sorting
                :items="items"
                :fields="roleFields"
                class="user-roles-table"
                @sort-changed="sortingChanged"
            >
                <!-- status slot -->
                <template slot="status" slot-scope="data">
                    <base-status :status-type="data.item.status"></base-status>
                </template>
                <!-- action slot -->
                <template slot="action" slot-scope="data">
                    <div
                        :class="[
                            {
                                disabled:
                                    data.item.status == 'locked' ||
                                    !hasScopes('manage-roles') ||
                                    user.roleId === data.item._id,
                            },
                            'action-review',
                        ]"
                    >
                        <!-- if action modify -->
                        <base-action
                            :class="{
                                disabled: data.item.status == 'locked',
                            }"
                            icon="icon-edit"
                            label="Modify"
                            @click="
                                selectedRecord(data.item)
                                $bvModal.show('add-role-popup')
                            "
                        ></base-action>
                        <!-- if action archive -->
                        <base-action
                            v-if="
                                data.item.status == 'active' ||
                                data.item.status == 'locked'
                            "
                            icon="icon-trash"
                            label="Archive"
                            @click="
                                selectedRecord(data.item)
                                $bvModal.show('archive-popup')
                            "
                        ></base-action>
                        <!-- if action activate -->
                        <base-action
                            v-if="data.item.status == 'archived'"
                            icon="icon-done"
                            label="Activate"
                            @click="
                                selectedRecord(data.item)
                                $bvModal.show('activate-popup')
                            "
                        ></base-action>
                    </div>
                </template>
            </b-table>
            <!-- When data is loading -->
            <base-table-spinner
                v-if="items.length == 0 && showPageLoader"
            ></base-table-spinner>
            <!-- When record not found -->
            <base-no-record-found
                v-if="items.length == 0 && !showPageLoader"
            ></base-no-record-found>
        </div>
        <pagination
            v-if="!showPageLoader"
            ref="pagination"
            :pagination="pagination"
            @pagination-changed="paginationChanged"
            @button-clicked-previous="applySearch('previous')"
            @button-clicked-next="applySearch('next')"
        ></pagination>
        <add-role-popup></add-role-popup>
        <archive-popup
            ref="archive-popup"
            record-type="role"
            @update-status="updateStatus"
        ></archive-popup>
        <activate-popup
            ref="activate-popup"
            record-type="role"
            @update-status="updateStatus"
        ></activate-popup>
    </div>
</template>

<script>
import addRolePopup from '@/components/popups/AddRolePopup.vue'
import archivePopup from '@/components/popups/ArchivePopup.vue'
import pagination from '@/components/Pagination.vue'
import activatePopup from '@/components/popups/ActivatePopup.vue'
import Resource from '@/api/resource'
const apiResource = new Resource('access-control/roles/')

import { mapGetters } from 'vuex'

export default {
    components: {
        addRolePopup,
        archivePopup,
        pagination,
        activatePopup,
    },

    /*
    |--------------------------------------------------------------------------
    | Component > props
    |--------------------------------------------------------------------------
    */

    props: {}, // End of Component > props

    /*
    |--------------------------------------------------------------------------
    | Component > data
    |--------------------------------------------------------------------------
    */
    data() {
        return {
            query: {
                keyword: '',
                sortOrder: '',
                sortBy: '',
                startAt: 1,
                perPage: 10,
            },
            rolesRecords: [],
            items: [],
            showPageLoader: true,
            pagination: {},
        }
    }, // End of Component > data

    /*
    |--------------------------------------------------------------------------
    | Component > computed
    |--------------------------------------------------------------------------
    */
    computed: {
        ...mapGetters([
            'roleFields',
            'rolesList',
            'selectedItem',
            'reload',
            'loading',
            'hasScopes',
            'user',
        ]),
    }, // End of Component > computed

    /*
    |--------------------------------------------------------------------------
    | Component > watch
    |--------------------------------------------------------------------------
    */
    watch: {
        reload(value) {
            if (value) {
                this.items = []
                this.query.startAt = 1
                this.getRecords()
            }
        },
    }, // End of Component > watch
    /*
    |--------------------------------------------------------------------------
    | Component > mounted
    |--------------------------------------------------------------------------
    */
    mounted() {
        this.getRecords()
    }, // End of Component > mounted
    /*
    |--------------------------------------------------------------------------
    | Component > methods
    |--------------------------------------------------------------------------
    */
    methods: {
        async getRecords() {
            this.showPageLoader = true
            let { data } = await apiResource.list(this.query)
            this.pagination = data.pagination
            this.pagination.totalRecords = data.data.length
            this.pagination.perPage = this.query.perPage
            this.items = data.data
            localStorage.setItem('rolesList', JSON.stringify(this.items))
            this.showPageLoader = false
            this.$store.commit('setLoading', false)
        }, // End of getRecords() method

        sortingChanged(ctx) {
            // ctx.sortBy ==> Field key for sorting by (or null for no sorting)
            this.query.sortBy = ctx.sortBy
            // ctx.sortDesc ==> true if sorting descending, false otherwise
            this.query.sortOrder = ctx.sortDesc ? 'desc' : 'asc'
            this.getRecords()
        }, // End of sortingChanged() method

        selectedRecord(record) {
            this.$store.commit('setItem', record)
        }, // End of selectedRecord() method

        async updateStatus(status) {
            let params = {
                id: this.selectedItem.id,
                title: this.selectedItem.title,
                status: status,
                permissions: this.selectedItem.permissions,
            }
            await this.$store.dispatch('updateRole', params)
        }, // End of updateStatus() method

        paginationChanged(type) {
            const perPage = type || 10
            this.query.perPage = perPage
            this.query.startAt = 1
            this.getRecords()
        }, // End of paginationChanged() method

        applySearch(type) {
            if (type && type == 'next') {
                this.query.startAt = this.pagination.next
            }

            if (type && type == 'previous') {
                this.query.startAt = this.pagination.current - 1
            }

            if (type && type == 'search') {
                this.query.startAt = 1
            }
            this.items = []
            this.showPageLoader = true
            if (type && type == 'search') {
                this.$store.commit('setLoading', true)
            }
            this.getRecords()
        }, // End of applySearch() method
    }, // End of Component > methods
} // End of export default
</script>
